import React, { useState } from "react";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Button, Row, Col } from "react-bootstrap";
import { FiltersButton, InfoBanner, TableHeader, WithRoleAccess } from "@services/ui-components";
import { SearchComponent } from "../../Services/SearchComponent/SearchComponent";
import { ErrorModalComponent } from "@me-pos/error-modal";
import CategoriesDishesTable from "./CategoriesDishesTable";
import { CategoryFormModalState } from "./interfaces";
import MenuLanguageVersionSelectContainer from "../components/LanguageVersionSelect/MenuLanguageVersionSelectContainer";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

const initialModalState: CategoryFormModalState = {
    isOpen: false,
    mode: "create",
    category: null,
    parentCategory: null,
};
const CategoriesDishesPage: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canCreateDishCategory = useRoleAccess(CompanyAccess.MenuDishCategoryCreate);
    const [showFilters, setShowFilters] = useState(false);
    const [searchValue, setSearchValue] = useState<string | null>(null);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [modalCategoryState, setModalCategoryState] = useState(initialModalState);

    const toggleFilters = () => setShowFilters(!showFilters);

    const handleSearchChange = (value: string) => {
        setSearchValue(value || null);
    };

    const openCategoryModal = () => {
        if (!canCreateDishCategory) return;
        setModalCategoryState({
            ...initialModalState,
            isOpen: true,
        });
    };

    return (
        <div className="mb-4">
            <InfoBanner>
                <p className="fs-7 mb-0">
                    {t(
                        "On this page, you can create new categories and subcategories of dishes or edit existing ones."
                    )}
                </p>
            </InfoBanner>
            <TableHeader
                title={t("Category of dishes")}
                paths={[
                    { path: `/branches/${branchId}`, label: t("Branches") },
                    { path: "", label: t("Category of dishes") },
                ]}
                buttons={
                    <Row className="g-3 justify-content-end mt-3 mt-lg-0">
                        <Col xs={12} sm={5} xxl={4} style={{ minWidth: "226px" }}>
                            <div className="position-relative">
                                <span className="position-absolute start-0 top-0 z-1 ps-6 pt-1 fs-8 text-grey">
                                    {t("Language version")}
                                </span>
                                <MenuLanguageVersionSelectContainer companyId={Number(currentCompanyId)} />
                            </div>
                        </Col>
                        <WithRoleAccess permission={CompanyAccess.MenuDishCategoryCreate}>
                            <Col xs={9} sm={5} xxl={4}>
                                <Button
                                    variant="primary"
                                    className="fw-normal w-100 rounded-1 fw-bold text-truncate"
                                    onClick={openCategoryModal}
                                >
                                    <i className="bi bi-plus-lg me-2"></i>
                                    {t("Add Category")}
                                </Button>
                            </Col>
                        </WithRoleAccess>
                        <Col xs={3} sm={2}>
                            <FiltersButton
                                filter={{ searchValue: searchValue }}
                                showFilters={showFilters}
                                onClick={toggleFilters}
                                classesText="d-none d-xxl-inline-block"
                            />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
                colSize={5}
                btnColSize={7}
            >
                <SearchComponent
                    value={searchValue}
                    placeholder={t("Search by category/subcategory name")}
                    searchHandling={handleSearchChange}
                />
            </TableHeader>
            <CategoriesDishesTable
                searchValue={searchValue}
                modalCategoryState={modalCategoryState}
                setModalCategoryState={setModalCategoryState}
                openCategoryModal={openCategoryModal}
            />

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </div>
    );
};

export default CategoriesDishesPage;
